/**
 * field-star — does this field render the required ✱? (s178, A3; D-176.20)
 *
 * 🔴 THE RULE: the star means "this field counts toward completion". So star-state must derive
 * from the SAME source the completion denominator uses, not from a second signal that happens to
 * agree most of the time.
 *
 * WHAT WENT WRONG. The star read `generic_fields.is_required` — a DB column — while
 * `severityOptional` is spec-only with NO DB column (D-155.1). A `severityOptional` field keeps
 * `isRequired` (a plausible future consumer) but LEAVES the completion denominator, so the two
 * signals diverged the moment the set became non-empty. It held exactly two members from s176
 * (`spouse_dob`, `place_of_birth`), and both rendered a ✱ they did not earn — under a dashboard
 * footnote reading *"Fields marked ✱ count toward completion"*, which was therefore false for
 * them. The star was making a promise the math did not keep.
 *
 * 🔴 The footnote needs NO edit once this ships: fixing the star makes the sentence true again.
 * That is the shape to prefer — repair the signal, not the caption explaining the broken signal.
 *
 * WHY A FUNCTION AND NOT A CONVENTION. Nine render sites draw this star across three layouts.
 * A rule that each of them has to remember is not a rule (D-140), so they all call this, and
 * `star-denominator-agreement.test.ts` asserts this predicate agrees with the field filter in
 * `computeSectionProgress` — the cross-file agreement guard shape ruled in D-176.19.
 */
import { isSeverityOptionalField } from "../constants/severity-optional-fields";

export interface StarField {
  fieldKey: string;
  isRequired?: boolean | null;
}

/**
 * Does this field render the required star?
 *
 * True iff it is required AND still counts toward completion. Mirrors the denominator's own
 * filter (`isRequired && severityOptional !== true`) in `shared/section-progress.ts`.
 *
 * NOTE the deliberate omission: Cat-A fields also drop out of the completion denominator, but
 * they KEEP their star. That is not an inconsistency — a Cat-A star is the affordance telling the
 * planner the field matters, and it was carved out of the denominator only because its value can
 * never reach the server to be counted (A-3, s114). The star is still honest there; the
 * `severityOptional` star was not.
 */
export function fieldRendersStar(sectionSlug: string, field: StarField): boolean {
  if (!field.isRequired) return false;
  return !isSeverityOptionalField(sectionSlug, field.fieldKey);
}
